import { useState } from "react";
import { AlertCircle, RefreshCw, ShoppingBag } from "lucide-react";

import { KpiCard } from "@/components/KpiCard";
import { formatDateTime, formatNumber } from "@/lib/format";

import { useWebstoreBusinesses, useWebstoreOverview } from "../api/webstoreAnalyticsApi";
import { BreakdownList } from "../components/BreakdownList";
import { BusinessTable } from "../components/BusinessTable";
import { EventTable } from "../components/EventTable";
import { TopPagesTable } from "../components/TopPagesTable";
import { TopPropertyTable } from "../components/TopPropertyTable";
import { TrafficSourceTable } from "../components/TrafficSourceTable";
import { WebstoreFilters } from "../components/WebstoreFilters";
import { WebstoreFunnel } from "../components/WebstoreFunnel";
import { WebstoreNotes } from "../components/WebstoreNotes";
import { WebstoreTrendChart } from "../components/WebstoreTrendChart";
import {
  DateRangePreset,
  countryLabel,
  formatDuration,
  getDateRangeForPreset,
} from "../utils/dateRanges";

/**
 * Webstore Analytics — product usage for Webstore v3.
 *
 * Reads the product-analytics service's view layer through
 * /api/webstore-analytics. Unlike the marketing pages in this dashboard it is not
 * backed by the monthly pipeline run, so it carries its own header and date range
 * and Layout skips the month picker for this route.
 */
export function WebstoreAnalyticsOverview() {
  const [preset, setPreset] = useState<DateRangePreset>("last-30");
  const [dates, setDates] = useState(() => getDateRangeForPreset("last-30"));
  const [businessId, setBusinessId] = useState("all");

  const handlePresetChange = (next: DateRangePreset) => {
    setPreset(next);
    if (next !== "custom") setDates(getDateRangeForPreset(next));
  };

  const handleCustomDates = (startDate: string, endDate: string) => {
    setDates({ startDate, endDate });
  };

  const { data, error, isLoading, isFetching, refetch } = useWebstoreOverview(
    dates.startDate,
    dates.endDate,
    businessId
  );

  const {
    data: businessesData,
    isLoading: businessesLoading,
    isFetching: businessesFetching,
    refetch: refetchBusinesses,
  } = useWebstoreBusinesses(dates.startDate, dates.endDate);

  const isBusy = isLoading || isFetching || businessesFetching;

  /**
   * Refresh everything the tab shows, not just the panels.
   *
   * The business list is a second query with its own cache entry, so refetching only
   * the overview left the filter dropdown showing a stale list — a storefront that
   * had just started sending events would not appear in it until the whole page was
   * reloaded and the react-query cache dropped.
   */
  const refreshAll = () => {
    refetch();
    refetchBusinesses();
  };
  const summary = data?.summary;

  // 400 means someone has to set an env var; 502 means retry. Different sentence.
  const isConfigError =
    !!error && error.message.toLowerCase().includes("not configured");

  const selectedBusiness = businessesData?.businesses.find(
    (b) => b.business_id === businessId
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-border pb-4">
        <div>
          <h1 className="text-xl font-bold text-ink-900 flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-slate-400" strokeWidth={1.75} />
            Webstore Analytics
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Product usage across Webstore v3 storefronts
            {selectedBusiness ? ` · ${selectedBusiness.business_id}` : ""}
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
              Updated: {formatDateTime(data.updated_at)}
            </span>
            <button
              type="button"
              onClick={refreshAll}
              disabled={isBusy}
              title="Refresh data"
              className="flex items-center justify-center p-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-800 disabled:bg-slate-50 disabled:text-slate-300 transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isBusy ? "animate-spin" : ""}`} />
            </button>
          </div>
        )}
      </div>

      <WebstoreFilters
        preset={preset}
        startDate={dates.startDate}
        endDate={dates.endDate}
        onPresetChange={handlePresetChange}
        onCustomDatesChange={handleCustomDates}
        businessId={businessId}
        onBusinessChange={setBusinessId}
        businesses={businessesData?.businesses ?? []}
        businessesLoading={businessesLoading}
      />

      {isBusy && !data ? (
        <LoadingSkeleton />
      ) : error ? (
        <div className="panel p-8 text-center max-w-lg mx-auto">
          <AlertCircle className="h-10 w-10 text-accent-red mx-auto mb-4" />
          <h3 className="text-base font-semibold text-slate-900">
            {isConfigError ? "Configuration required" : "Couldn't load product analytics"}
          </h3>
          <p className="text-xs text-slate-500 mt-2 max-w-sm mx-auto leading-relaxed">
            {isConfigError
              ? "This tab reads the product-analytics Postgres directly. Set WEBSTORE_ANALYTICS_DATABASE_URL to its read-only connection string and restart the API."
              : "The product-analytics database did not answer. It may be down, or the reporting user may have lost SELECT on the views."}
          </p>
          <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600 font-mono text-left max-h-40 overflow-y-auto">
            {error.message}
          </div>
          {!isConfigError && (
            <button
              type="button"
              onClick={refreshAll}
              className="mt-6 px-4 py-2 bg-accent-blue hover:bg-accent-blue/90 text-white rounded text-xs font-semibold shadow-sm transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      ) : data && summary ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Sessions"
              value={formatNumber(summary.sessions)}
              accent="blue"
              sublabel={`${formatNumber(summary.new_visitor_sessions)} new · ${formatNumber(
                summary.returning_visitor_sessions
              )} returning`}
              tooltip="Visits that started in this range. A visit ends after 30 minutes of inactivity."
            />
            <KpiCard
              label="Unique Visitors"
              value={formatNumber(summary.unique_visitors)}
              accent="cyan"
              sublabel="Distinct devices"
              tooltip="Counted once across the whole range, so it is not the sum of the daily figures."
            />
            <KpiCard
              label="Page Views"
              value={formatNumber(summary.page_views)}
              accent="teal"
              sublabel={`${formatNumber(summary.total_events)} events total`}
              tooltip="page_view events. The events figure counts every tracked action, page views included."
            />
            <KpiCard
              label="Bounce Rate"
              value={summary.bounce_rate_pct == null ? "—" : `${summary.bounce_rate_pct}%`}
              accent={
                summary.bounce_rate_pct != null && summary.bounce_rate_pct > 60 ? "amber" : "green"
              }
              sublabel="Visits with a single page view"
              tooltip="Defined in the product-analytics v_session view, so this matches Metabase exactly."
            />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Avg. Visit Length"
              value={formatDuration(summary.avg_duration_seconds)}
              accent="violet"
              sublabel="First to last event in a visit"
              tooltip="Single-event visits count as zero seconds — there is no second timestamp to measure against."
            />
            <KpiCard
              label="Logged-in Sessions"
              value={formatNumber(summary.logged_in_sessions)}
              accent="green"
              sublabel={`${
                summary.sessions
                  ? Math.round((summary.logged_in_sessions / summary.sessions) * 100)
                  : 0
              }% of sessions`}
              tooltip="Visits that carried a user id at any point. Login usually happens mid-visit, so the whole visit counts."
            />
            <KpiCard
              label="Storefronts"
              value={formatNumber(data.businesses.length)}
              accent="amber"
              sublabel="Businesses with traffic"
              tooltip="Distinct businesses that had at least one session in this range."
            />
            <KpiCard
              label="Event Types"
              value={formatNumber(summary.distinct_event_names)}
              accent="blue"
              sublabel={`${data.events.filter((e) => !e.is_known).length} unregistered`}
              tooltip="Distinct event names received. Unregistered names are stored and counted, but are missing from the gateway's app registry."
            />
          </div>

          <WebstoreTrendChart daily={data.daily} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <WebstoreFunnel funnel={data.funnel} />
            <EventTable events={data.events} />
          </div>

          <TopPagesTable pages={data.top_pages} />

          <BusinessTable
            businesses={data.businesses}
            selectedBusinessId={businessId}
            onSelectBusiness={setBusinessId}
          />

          <TrafficSourceTable sources={data.traffic_sources} />

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <BreakdownList
              title="Devices"
              subtitle="Sessions by device type"
              rows={data.devices}
              barClassName="bg-accent-blue"
            />
            <BreakdownList
              title="Browsers"
              subtitle="Sessions by browser"
              rows={data.browsers}
              barClassName="bg-accent-cyan"
            />
            <BreakdownList
              title="Operating systems"
              subtitle="Sessions by OS"
              rows={data.operating_systems}
              barClassName="bg-accent-teal"
            />
            <BreakdownList
              title="Countries"
              subtitle="Sessions by resolved country"
              rows={data.countries}
              barClassName="bg-accent-violet"
              formatKey={countryLabel}
              emptyLabel="No country could be resolved in this range"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <TopPropertyTable
              title="Most viewed items"
              subtitle="From view_item events"
              valueHeader="Item"
              countHeader="Views"
              rows={data.top_viewed_items}
              empty="No item views tagged with an item name in this range"
            />
            <TopPropertyTable
              title="Most added to cart"
              subtitle="From add_to_cart events"
              valueHeader="Item"
              countHeader="Adds"
              rows={data.top_added_items}
              empty="No cart additions tagged with an item name in this range"
            />
            <TopPropertyTable
              title="Top searches"
              subtitle="From search events"
              valueHeader="Term"
              countHeader="Searches"
              rows={data.top_searches}
              empty="No searches recorded in this range"
            />
          </div>

          <BreakdownList
            title="Storefront domains"
            subtitle="Which hostname visitors arrived on — makes a domain migration visible"
            rows={data.hostnames}
            limit={8}
            barClassName="bg-accent-amber"
          />

          <WebstoreNotes notes={data.notes} />
        </>
      ) : null}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="bg-white border border-border rounded-card h-28 px-5 py-4 flex flex-col justify-between"
          >
            <div className="h-3 w-20 bg-slate-200 rounded" />
            <div className="h-8 w-28 bg-slate-200 rounded mt-3" />
            <div className="h-3 w-16 bg-slate-100 rounded mt-2" />
          </div>
        ))}
      </div>
      <div className="bg-white border border-border rounded-card h-72" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border rounded-card h-96" />
        <div className="bg-white border border-border rounded-card h-96" />
      </div>
    </div>
  );
}
